// File: src/MathTools/PolynomialGraphTool.js
//----------------------------------------------------------

function parseEquation(eqString) {
  // eslint-disable-next-line no-new-func
  return new Function("x", `return (${eqString});`);
}

function tryRationalRoots(eqString, maxDen = 1) {
  // very naive approach: just tries integer roots from -10..10
  // or we could parse coefficients for a more robust approach
  // If eq is x**3 - 6*x**2 + ...
  // We'll just do a quick approach
  const fn = parseEquation(eqString);
  let found = [];
  for (let r = -10; r <= 10; r++) {
    const val = fn(r);
    if (Math.abs(val) < 1e-9) {
      found.push(r);
    }
  }
  return found;
}

export default function PolynomialGraphTool({
  equation = "x**2 - 4",
  displayEquation,
  xMin = -5,
  xMax = 5,
  step = 0.1,
  width = 400,
  height = 300,
  margin = 30,
  showAxes = true,
  showTicks = true,
  maxTicks = 20,
  showRoots = false
}) {
  // 1) parse eq
  const fn = parseEquation(equation);

  // 2) sample points
  let points = [];
  for (let x = xMin; x <= xMax; x += step) {
    const y = fn(x);
    points.push({x, y});
  }
  if (!points.length) return <div>No points to plot.</div>;

  // find yMin..yMax
  let yMin = Infinity, yMaxi = -Infinity;
  points.forEach(p => {
    if (p.y < yMin) yMin = p.y;
    if (p.y > yMaxi) yMaxi = p.y;
  });
  // pad
  const padY = 0.1*(yMaxi - yMin);
  yMin -= padY;
  yMaxi += padY;

  // map
  const usableW = width -2*margin;
  const usableH = height -2*margin;
  function mapX(val) {
    const ratio = (val - xMin)/(xMax - xMin);
    return margin + ratio*usableW;
  }
  function mapY(val) {
    const ratio = (val - yMin)/(yMaxi - yMin);
    return margin + (1-ratio)*usableH;
  }

  // path
  const pathD = points.map((pt,i) => {
    const sx = mapX(pt.x);
    const sy = mapY(pt.y);
    return i===0 ? `M ${sx},${sy}` : `L ${sx},${sy}`;
  }).join(" ");

  // optional root finding
  let rootCircles = [];
  if (showRoots) {
    const possibleRoots = tryRationalRoots(equation);
    rootCircles = possibleRoots.map((r,i) => {
      // only show if r in [xMin..xMax]
      if (r < xMin || r > xMax) return null;
      const sy = mapY(fn(r)); // should be near 0 if it's truly a root
      const sx = mapX(r);
      return (
        <circle
          key={`root-${i}`}
          cx={sx}
          cy={sy}
          r={5}
          fill="red"
        />
      );
    });
  }

  // minimal axes
  let xAxisLine=null, yAxisLine=null;
  if (showAxes) {
    if (yMin < 0 && yMaxi > 0) {
      xAxisLine = (
        <line
          x1={mapX(xMin)} y1={mapY(0)}
          x2={mapX(xMax)} y2={mapY(0)}
          stroke="gray"
          strokeWidth={1}
        />
      );
    }
    if (xMin < 0 && xMax > 0) {
      yAxisLine=(
        <line
          x1={mapX(0)} y1={mapY(yMin)}
          x2={mapX(0)} y2={mapY(yMaxi)}
          stroke="gray"
          strokeWidth={1}
        />
      );
    }
  }

  return (
    <div style={{ textAlign:"center" }}>
      <svg width={width} height={height} style={{ border:"1px solid #ccc"}}>
        {xAxisLine}
        {yAxisLine}
        <path d={pathD} fill="none" stroke="blue" strokeWidth={2}/>
        {rootCircles}
      </svg>
      <div style={{marginTop:4, fontSize:"0.9em"}}>
        <em>PolynomialGraphTool for eqn: {displayEquation}</em>
      </div>
    </div>
  );
}
